import '@aws-amplify/ui-react/styles.css'
import '@/css/main.css'
import '@/features/localization/init'

import { LicenseInfo } from '@mui/x-license'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { logExceptionInCoralogix } from '@/features/coralogix/utils/sendExceptions'
import { ThemeProvider } from '@/features/whitelabel/context/WhitelabelContext'

import App from './App'
import { PageMetadataContextProvider } from './components/layouts/PageMetadataContext'
import environment from './environment'
import AmplifySetupWrapper from './features/authentication/components/AmplifySetupWrapper'
import { AuthProvider } from './features/authentication/contexts/AuthContext'
import { FeatureToggleProvider } from './features/featureToggle/contexts/FeatureToggleContext'

const MUI_PRO_PLAN_LICENSE_KEY = environment.getMuiProLicenseKey()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 0,
      refetchOnWindowFocus: false,
    },
  },
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      const labels: Record<string, unknown> = {}
      if (mutation.options.mutationKey) {
        labels.mutation_key = (Array.from(mutation.options.mutationKey) as string[]).join(':')
      }
      logExceptionInCoralogix(err, { mutationId: mutation.mutationId, variables: mutation.state.variables }, labels)
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      logExceptionInCoralogix(
        err,
        { queryHash: query.queryHash },
        { query_fingerprint: query.queryHash.replaceAll(/\d/g, '0') },
      )
    },
  }),
})

// Set the license for the MUI pro plan
if (MUI_PRO_PLAN_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(MUI_PRO_PLAN_LICENSE_KEY)
}

// TanStack Query browser-extension bridge — dev-only.
// Exposes the QueryClient on `window.__TANSTACK_QUERY_CLIENT__` so the
// Chrome / Firefox "TanStack Query DevTools" extension can attach to the
// page.
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__?: QueryClient
  }
}

if (import.meta.env.DEV) {
  window.__TANSTACK_QUERY_CLIENT__ = queryClient
}

// TanStack Query DevTools (embedded floating panel) — dev-only.
const ReactQueryDevtools = import.meta.env.PROD
  ? null
  : lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AmplifySetupWrapper>
          <AuthProvider>
            <PageMetadataContextProvider>
              <ThemeProvider>
                <FeatureToggleProvider>
                  <App />
                </FeatureToggleProvider>
              </ThemeProvider>
            </PageMetadataContextProvider>
          </AuthProvider>
        </AmplifySetupWrapper>
      </BrowserRouter>
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  </StrictMode>,
)
